import { Dayjs } from 'dayjs';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import { InvoiceProvider } from './invoice-provider.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { TimeZone } from '../../../time/timezone.js';
import { isSameList } from '../../dto/data-model-utils.js';
import { Option } from '../../../utility/option.js';
import { VersionRange } from '../../core/version-range.js';

export class LegacyReceipt extends DataModel {
  static readonly TYPE_ID: string = '25145629ce98439a803b3350cdd66414';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly uniqueId: string,
    public readonly name: string,
    public readonly provider: InvoiceProvider,
    public readonly downloadUrl: string,
    public readonly cancelledDownloadUrl: string | null,
    public readonly isCancelled: boolean,
    public readonly isElectronic: boolean,
    public readonly emailAddresses: readonly string[],
    public readonly creationDate: Dayjs,
    public readonly cancellationDate: Dayjs | null,
  ) {
    super();
  }

  get $typeId(): string {
    return LegacyReceipt.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, LegacyReceipt.SCHEMA_VERSION);
  }

  get sanitizedName(): string | undefined {
    let name = this.name;

    if(!name) {
      return name;
    }

    name = name.normalize('NFD').replace(/\p{Diacritic}/gu, '');
    return name.replace(/[^a-zA-Z0-9]/g, '_');
  }

  static create(args: {
    uniqueId: string,
    name: string,
    provider: InvoiceProvider,
    downloadUrl: string,
    cancelledDownloadUrl: string | null,
    isCancelled: boolean,
    isElectronic: boolean,
    emailAddresses: readonly string[],
    creationDate: Dayjs,
    cancellationDate: Dayjs | null,
  }): LegacyReceipt {
    return new LegacyReceipt(
        args.uniqueId,
        args.name,
        args.provider,
        args.downloadUrl,
        args.cancelledDownloadUrl,
        args.isCancelled,
        args.isElectronic,
        args.emailAddresses,
        args.creationDate,
        args.cancellationDate,
    );
  }

  static electronic(args: {
    uniqueId: string,
    name: string,
    provider: InvoiceProvider,
    downloadUrl: string,
    emailAddresses: readonly string[],
    creationDate: Dayjs,
  }): LegacyReceipt {
    return LegacyReceipt.create({
      uniqueId: args.uniqueId,
      name: args.name,
      provider: args.provider,
      downloadUrl: args.downloadUrl,
      cancelledDownloadUrl: null,
      isCancelled: false,
      isElectronic: true,
      emailAddresses: args.emailAddresses,
      creationDate: args.creationDate,
      cancellationDate: null,
    });
  }

  static physical(args: {
    uniqueId: string,
    name: string,
    provider: InvoiceProvider,
    downloadUrl: string,
    creationDate: Dayjs,
  }): LegacyReceipt {
    return LegacyReceipt.create({
      uniqueId: args.uniqueId,
      name: args.name,
      provider: args.provider,
      downloadUrl: args.downloadUrl,
      cancelledDownloadUrl: null,
      isCancelled: false,
      isElectronic: false,
      emailAddresses: [],
      creationDate: args.creationDate,
      cancellationDate: null,
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyReceipt {
    return LegacyReceipt.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      name: MapUtils.getString(map, 'name'),
      provider: toEnumFromString(MapUtils.getString(map, 'provider'), InvoiceProvider),
      downloadUrl: MapUtils.getString(map, 'downloadUrl'),
      cancelledDownloadUrl: MapUtils.getStringOrNull(map, 'cancelledDownloadUrl'),
      isCancelled: MapUtils.getBoolean(map, 'isCancelled'),
      isElectronic: MapUtils.getBoolean(map, 'isElectronic'),
      emailAddresses: MapUtils.getList(map, 'emailAddresses', (x) => JSONUtils.decodeString(x)),
      creationDate: context.decodeDate(MapUtils.getNumber(map, 'creationDate')),
      cancellationDate: context.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'cancellationDate')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'name': this.name,
      'provider': fromEnumAsString(this.provider),
      'downloadUrl': this.downloadUrl,
      'cancelledDownloadUrl': this.cancelledDownloadUrl,
      'isCancelled': this.isCancelled,
      'isElectronic': this.isElectronic,
      'emailAddresses': this.emailAddresses.map((x) => x),
      'creationDate': context.encodeDate(this.creationDate),
      'cancellationDate': context.encodeDateOrNull(this.cancellationDate),
    };
  }

  clone(): this {
    return LegacyReceipt.create({
      uniqueId: this.uniqueId,
      name: this.name,
      provider: this.provider,
      downloadUrl: this.downloadUrl,
      cancelledDownloadUrl: this.cancelledDownloadUrl,
      isCancelled: this.isCancelled,
      isElectronic: this.isElectronic,
      emailAddresses: this.emailAddresses.map((x) => x),
      creationDate: TimeZone.cloneDate(this.creationDate),
      cancellationDate: TimeZone.cloneDateOrNull(this.cancellationDate),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.name === this.name &&
        other.provider === this.provider &&
        other.downloadUrl === this.downloadUrl &&
        other.cancelledDownloadUrl === this.cancelledDownloadUrl &&
        other.isCancelled === this.isCancelled &&
        other.isElectronic === this.isElectronic &&
        isSameList(other.emailAddresses, this.emailAddresses) &&
        other.creationDate === this.creationDate &&
        other.cancellationDate === this.cancellationDate;
  }

  copyWith(args: {
    uniqueId: Option<string>,
    name: Option<string>,
    provider: Option<InvoiceProvider>,
    downloadUrl: Option<string>,
    cancelledDownloadUrl: Option<string | null>,
    isCancelled: Option<boolean>,
    isElectronic: Option<boolean>,
    emailAddresses: Option<readonly string[]>,
    creationDate: Option<Dayjs>,
    cancellationDate: Option<Dayjs | null>,
  }): LegacyReceipt {
    return LegacyReceipt.create({
      uniqueId: args.uniqueId.getOrElse(this.uniqueId),
      name: args.name.getOrElse(this.name),
      provider: args.provider.getOrElse(this.provider),
      downloadUrl: args.downloadUrl.getOrElse(this.downloadUrl),
      cancelledDownloadUrl: args.cancelledDownloadUrl.getOrElse(this.cancelledDownloadUrl),
      isCancelled: args.isCancelled.getOrElse(this.isCancelled),
      isElectronic: args.isElectronic.getOrElse(this.isElectronic),
      emailAddresses: args.emailAddresses.getOrElse(this.emailAddresses),
      creationDate: args.creationDate.getOrElse(TimeZone.cloneDate(this.creationDate)),
      cancellationDate: args.cancellationDate.getOrElse(TimeZone.cloneDateOrNull(this.cancellationDate)),
    });
  }

  copyAsCancelledOrClone(cancelledDownloadUrl: string, cancellationDate: Dayjs): LegacyReceipt {
    if(this.isCancelled) {
      return this.clone();
    }

    return this.copyWith({
      isCancelled: Option.some(true),
      cancellationDate: Option.some(cancellationDate),
      creationDate: Option.none(),
      downloadUrl: Option.none(),
      cancelledDownloadUrl: Option.some(cancelledDownloadUrl),
      emailAddresses: Option.none(),
      isElectronic: Option.none(),
      name: Option.none(),
      provider: Option.none(),
      uniqueId: Option.none(),
    });
  }
}

export class LegacyReceiptBuilder extends DataModelBuilder<LegacyReceipt> {
  build(context: SerializationContext, map: SerializedData): LegacyReceipt {
    return LegacyReceipt.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(LegacyReceipt.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LegacyReceipt.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version we added `cancelledDownloadUrl` of type `string | null`.
    if(version < 2) version = 2;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
